// Pure tz-aware date helpers used by accounting. Intl-based; no new dep.

export type AccountingTz = 'utc' | 'local'

export const getTzAbbr = (tz: AccountingTz, at: Date = new Date()): string => {
  if (tz === 'utc') return 'UTC'
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZoneName: 'short',
  }).formatToParts(at)
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? 'UTC'
}

export const startOfDay = (d: Date, tz: AccountingTz): Date =>
  tz === 'utc'
    ? new Date(
        Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0),
      )
    : new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)

export const endOfDay = (d: Date, tz: AccountingTz): Date =>
  tz === 'utc'
    ? new Date(
        Date.UTC(
          d.getUTCFullYear(),
          d.getUTCMonth(),
          d.getUTCDate(),
          23,
          59,
          59,
          999,
        ),
      )
    : new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)

// ISO week: Monday as first day.
export const startOfWeek = (d: Date, tz: AccountingTz): Date => {
  const sd = startOfDay(d, tz)
  const dow = tz === 'utc' ? sd.getUTCDay() : sd.getDay()
  return addDays(sd, -((dow + 6) % 7), tz)
}

export const endOfWeek = (d: Date, tz: AccountingTz): Date =>
  endOfDay(addDays(startOfWeek(d, tz), 6, tz), tz)

export const startOfMonth = (d: Date, tz: AccountingTz): Date =>
  tz === 'utc'
    ? new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1))
    : new Date(d.getFullYear(), d.getMonth(), 1)

export const endOfMonth = (d: Date, tz: AccountingTz): Date =>
  tz === 'utc'
    ? new Date(
        Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0, 23, 59, 59, 999),
      )
    : new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999)

export const startOfQuarter = (d: Date, tz: AccountingTz): Date => {
  const month = tz === 'utc' ? d.getUTCMonth() : d.getMonth()
  const year = tz === 'utc' ? d.getUTCFullYear() : d.getFullYear()
  const firstMonth = Math.floor(month / 3) * 3
  return tz === 'utc'
    ? new Date(Date.UTC(year, firstMonth, 1))
    : new Date(year, firstMonth, 1)
}

export const endOfQuarter = (d: Date, tz: AccountingTz): Date =>
  endOfMonth(addMonths(startOfQuarter(d, tz), 2, tz), tz)

export const startOfYear = (d: Date, tz: AccountingTz): Date =>
  tz === 'utc'
    ? new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
    : new Date(d.getFullYear(), 0, 1)

export const endOfYear = (d: Date, tz: AccountingTz): Date =>
  tz === 'utc'
    ? new Date(Date.UTC(d.getUTCFullYear(), 11, 31, 23, 59, 59, 999))
    : new Date(d.getFullYear(), 11, 31, 23, 59, 59, 999)

export const addDays = (d: Date, n: number, tz: AccountingTz): Date =>
  tz === 'utc'
    ? new Date(
        Date.UTC(
          d.getUTCFullYear(),
          d.getUTCMonth(),
          d.getUTCDate() + n,
          d.getUTCHours(),
          d.getUTCMinutes(),
          d.getUTCSeconds(),
          d.getUTCMilliseconds(),
        ),
      )
    : new Date(
        d.getFullYear(),
        d.getMonth(),
        d.getDate() + n,
        d.getHours(),
        d.getMinutes(),
        d.getSeconds(),
        d.getMilliseconds(),
      )

export const addMonths = (d: Date, n: number, tz: AccountingTz): Date =>
  tz === 'utc'
    ? new Date(
        Date.UTC(
          d.getUTCFullYear(),
          d.getUTCMonth() + n,
          d.getUTCDate(),
          d.getUTCHours(),
          d.getUTCMinutes(),
          d.getUTCSeconds(),
          d.getUTCMilliseconds(),
        ),
      )
    : new Date(
        d.getFullYear(),
        d.getMonth() + n,
        d.getDate(),
        d.getHours(),
        d.getMinutes(),
        d.getSeconds(),
        d.getMilliseconds(),
      )

export const addYears = (d: Date, n: number, tz: AccountingTz): Date =>
  addMonths(d, n * 12, tz)

export const parseYmdInTz = (ymd: string, tz: AccountingTz): Date => {
  const [y, m, d] = ymd.split('-').map(Number)
  if (!y || !m || !d) return new Date(NaN)
  return tz === 'utc'
    ? new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0))
    : new Date(y, m - 1, d, 0, 0, 0, 0)
}

export const formatYmdInTz = (d: Date, tz: AccountingTz): string => {
  const y = tz === 'utc' ? d.getUTCFullYear() : d.getFullYear()
  const m = (tz === 'utc' ? d.getUTCMonth() : d.getMonth()) + 1
  const day = tz === 'utc' ? d.getUTCDate() : d.getDate()
  return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

// react-datepicker hands us a local-midnight Date. Treat its Y-M-D triple as
// the user's intent and reinterpret as midnight in tz.
export const datePickerValueToTz = (picked: Date, tz: AccountingTz): Date =>
  tz === 'utc'
    ? new Date(
        Date.UTC(
          picked.getFullYear(),
          picked.getMonth(),
          picked.getDate(),
          0,
          0,
          0,
          0,
        ),
      )
    : new Date(
        picked.getFullYear(),
        picked.getMonth(),
        picked.getDate(),
        0,
        0,
        0,
        0,
      )

// Inverse: produce a local-midnight Date the picker can render without shifting.
export const tzValueToDatePicker = (d: Date, tz: AccountingTz): Date => {
  const y = tz === 'utc' ? d.getUTCFullYear() : d.getFullYear()
  const m = tz === 'utc' ? d.getUTCMonth() : d.getMonth()
  const day = tz === 'utc' ? d.getUTCDate() : d.getDate()
  return new Date(y, m, day, 0, 0, 0, 0)
}

// Take a Date's Y/M/D as read in `fromTz` and return the midnight instant for
// that same calendar date in `toTz` — so flipping tz keeps the displayed date.
export const reinterpretYmdAcrossTz = (
  d: Date | undefined,
  fromTz: AccountingTz,
  toTz: AccountingTz,
): Date | undefined =>
  d ? parseYmdInTz(formatYmdInTz(d, fromTz), toTz) : undefined

export const sameDayInTz = (
  a: Date | undefined,
  b: Date | undefined,
  tz: AccountingTz,
): boolean => {
  if (!a && !b) return true
  if (!a || !b) return false
  return formatYmdInTz(a, tz) === formatYmdInTz(b, tz)
}
